import { useCallback, type ReactNode } from "react";
import { useStripe } from "@stripe/react-stripe-js";
import { useStripeIntentAuthentication } from "../hooks/useStripeIntentAuthentication";

export interface PaymentIntentAuthenticationProps {
  clientSecret: string;
  onAuthenticated: (authenticatedClientSecret: string) => Promise<void>;
  onProcessingChange?: (processing: boolean) => void;
  /** App-native presentation. Authentication and reconciliation stay in this
   * shared controller; only the rendered controls are supplied by the app. */
  render: (model: PaymentIntentAuthenticationRenderModel) => ReactNode;
}

export interface PaymentIntentAuthenticationRenderModel {
  phase:
    | "idle"
    | "confirming"
    | "authentication_failed"
    | "reconciling"
    | "reconciliation_failed"
    | "complete";
  busy: boolean;
  message: string;
  error: boolean;
  actionLabel: string;
  actionDisabled: boolean;
  authenticate: () => void;
}

/** Confirm an existing connected-account PaymentIntent, then reconcile it. */
export function PaymentIntentAuthentication({
  clientSecret,
  onAuthenticated,
  onProcessingChange,
  render,
}: PaymentIntentAuthenticationProps) {
  const stripe = useStripe();
  const confirmIntent = useCallback(async (): Promise<boolean> => {
    if (!stripe) return false;
    const result = await stripe.confirmCardPayment(clientSecret);
    return !result.error && result.paymentIntent?.status === "succeeded";
  }, [clientSecret, stripe]);
  const { phase, busy, authenticate } = useStripeIntentAuthentication({
    clientSecret,
    confirmIntent,
    onAuthenticated,
    onProcessingChange,
  });
  const isAuthenticationFailure = phase === "authentication_failed";
  const isReconciliationFailure = phase === "reconciliation_failed";
  const message = isAuthenticationFailure
    ? "We couldn't confirm this payment. Try again or choose another card."
    : isReconciliationFailure
      ? "Your payment was confirmed, but we couldn't finish the request. Try again."
      : phase === "reconciling"
        ? "Your payment was confirmed. Finishing your request."
        : phase === "complete"
          ? "Payment confirmed."
          : "Confirm this payment with your bank to finish checkout.";
  const actionLabel =
    phase === "confirming"
      ? "Confirming…"
      : phase === "reconciling"
        ? "Finishing…"
        : isAuthenticationFailure
          ? "Try again"
          : isReconciliationFailure
            ? "Finish request"
            : phase === "complete"
              ? "Payment confirmed"
              : "Confirm payment";
  const actionDisabled = !stripe || busy || phase === "complete";

  return render({
    phase,
    busy,
    message,
    error: isAuthenticationFailure || isReconciliationFailure,
    actionLabel,
    actionDisabled,
    authenticate: () => void authenticate(),
  });
}
